/** @jsxImportSource @emotion/react */
import { useDispatch, useSelector } from 'react-redux';
import { lazy, Suspense, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmarkCircle } from '@fortawesome/free-regular-svg-icons';
import { isArray } from 'lodash';
import { hooks as commonHooks } from '@sitebuilder/common';
import { chevronRightBlack } from '../../assets/images';
import { actionCreators } from '../../reducers/templateEditor';
import { StateShape as ReduxStateShape } from '../../reducers';
import { SpinnerIcon } from '../../components/BaseComponents';

const ObjItem = lazy(() => import('./ObjItem'));
const ArrayItem = lazy(() => import('./ArrayItem'));

interface ListItemProps {
    _key: string;
    value: any;
}

export const ListItem = (props: ListItemProps) => {
    const { _key, value } = props;
    const [isActive, setActiveState] = useState(false);

    const toggleCollapsible = () => {
        setActiveState(!isActive);
    };

    return (
        <div className="list-item">
            {value || typeof value === 'boolean' ? (
                <>
                    {typeof value !== 'object' ? (
                        <div className="text-value">
                            <span id="icon" className={`icon`}></span>
                            <span className="key-text">{_key}: </span>
                            <span className="value-text">
                                {' '}
                                {typeof value === 'number'
                                    ? value
                                    : typeof value === 'boolean'
                                      ? `${value}`
                                      : `"${value}"`}
                            </span>
                        </div>
                    ) : (
                        <>
                            {typeof value === 'object' && (
                                <>
                                    {isArray(value) ? (
                                        <>
                                            <div className="array-value">
                                                <img
                                                    id="accordion-icon"
                                                    className={`accordion-icon ${
                                                        isActive ? 'closed-icon' : ''
                                                    }`}
                                                    src={chevronRightBlack}
                                                    alt="accordion-icon"
                                                    onClick={toggleCollapsible}
                                                />
                                                <span id="icon" className={`icon`}></span>
                                                <span className="key-text">{_key}: </span>
                                                <span className="value-text">
                                                    {' '}
                                                    [{value.length}]
                                                </span>
                                            </div>
                                            {isActive && (
                                                <div className={`list-content`}>
                                                    <Suspense fallback={<SpinnerIcon />}>
                                                        <ArrayItem value={value} />
                                                    </Suspense>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div className="key-item">
                                                <img
                                                    id="accordion-icon"
                                                    className={`accordion-icon ${
                                                        isActive ? 'closed-icon' : ''
                                                    }`}
                                                    src={chevronRightBlack}
                                                    alt="accordion-icon"
                                                    onClick={toggleCollapsible}
                                                />
                                                <span id="icon" className={`icon`}></span>
                                                <span className="key-text">{_key} :</span>
                                                <span className="value-text">
                                                    {' '}
                                                    {`{${Object.keys(value)?.length}}`}
                                                </span>
                                            </div>
                                            {isActive && (
                                                <div className={`list-content`}>
                                                    <Suspense fallback={<SpinnerIcon />}>
                                                        <ObjItem value={value} />
                                                    </Suspense>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            ) : (
                <div className="text-value">
                    <span id="icon" className={`icon`}></span>
                    <span className="key-text">{_key}: </span>
                    <span className="value-text">
                        {' '}
                        {typeof value === 'undefined' ? 'undefined' : `""`}
                    </span>
                </div>
            )}
        </div>
    );
};

const DataPickerLayer = () => {
    const { data } = useSelector((state: ReduxStateShape) => {
        return {
            ...state.templateEditor,
        };
    });

    const i18n = commonHooks.useLocalization();
    const dispatch = useDispatch();

    const onClickClose = () => {
        dispatch(actionCreators.setIsShowDataPicker(false));
    };

    return (
        <div className="data-picker-layer">
            <div className="header">
                <h3 className="title">{i18n.t('data')}</h3>
                <button className="close-button" onClick={onClickClose}>
                    <FontAwesomeIcon icon={faXmarkCircle} />
                </button>
            </div>
            <div className="list">
                {data &&
                    Object.entries(data)?.map((entry, index) => {
                        return <ListItem key={index} _key={entry[0]} value={entry[1]} />;
                    })}
            </div>
        </div>
    );
};

export default DataPickerLayer;
